import { useMemo, useRef, useState } from 'react';
import { keccak256, encodeAbiParameters, toBytes, recoverMessageAddress } from 'viem';
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts';
import { computeClaimId, spokenCode } from './claimRecords';
import { eventHash, ZERO_HASH } from './caseTimeline';

const HOUR = 3600;
export const DEMO_FILE = { merchant: 'shop.example', firstStatusBy: 4, contestWindow: 72, claimTypes: ['cancellation'], endpoints: { claims: 'https://shop.example/claims' } };
const IMPOSTOR_ENDPOINT = 'https://shop-example-support.help/claims';
export const DEMO_ORDERS = ['ORD-20001', 'ORD-20002', 'RES-30001'];
export const CORE_FIELDS = ['claimId', 'caseRef', 'eventIndex', 'prevEventHash', 'status', 'missingInfo', 'updatedAt', 'nextUpdateBy', 'decisionCode', 'refundRoute', 'noteHash'];
const RECEIPT_TYPES = ['bytes32', 'bytes32', 'bytes32', 'uint64', 'uint64'].map((type) => ({ type }));
const now = () => Math.floor(Date.now() / 1000);

// Same stand-in as the reference kit: keccak256 of canonical JSON (sorted keys).
const canon = (o) => JSON.stringify(Object.keys(o).sort().reduce((a, k) => ({ ...a, [k]: o[k] }), {}));
export const requestHash = (d) => keccak256(toBytes(canon(d)));

export const receiptHash = (r) => keccak256(encodeAbiParameters(RECEIPT_TYPES, [
  r.claimId, r.requestHash, keccak256(toBytes(r.caseRef)), BigInt(r.receivedAt), BigInt(r.firstStatusBy),
]));

export const coreEvents = (events) => events.map((e) => CORE_FIELDS.reduce((a, k) => ({ ...a, [k]: e[k] }), {}));

function draftEvent(c, status, hours, extra = {}) {
  const t = now();
  const prev = c.events[c.events.length - 1];
  return {
    claimId: c.claimId, caseRef: c.caseRef, eventIndex: c.events.length + 1, prevEventHash: prev ? eventHash(prev) : ZERO_HASH,
    status, missingInfo: '', updatedAt: t, nextUpdateBy: hours ? t + hours * HOUR : 0, decisionCode: '', refundRoute: '', noteHash: ZERO_HASH, ...extra,
  };
}

export function deliveryState(c) {
  if (!c.agentHasReceipt) return { id: 'unconfirmed', label: 'Delivery unconfirmed', detail: 'No receipt reached the agent. The claim may still have arrived. Unconfirmed is not the same as failed. Retry with the same claim ID.' };
  if (!c.events.length) {
    const late = Math.floor(Date.now() / 1000) > c.receipt.firstStatusBy;
    return { id: 'unacknowledged', label: 'Delivered, unacknowledged', detail: late ? 'Overdue. The first-status time has passed with no "received" event.' : 'The receipt proves the claim arrived. No "received" event yet. It becomes overdue after the first-status time.' };
  }
  return { id: 'acknowledged', label: 'Acknowledged', detail: 'A receipt is held and the case has a "received" event.' };
}

const isDecision = (e) => ['approved', 'denied'].includes(e.status) && !e.decisionCode.startsWith('contest');

export function useClaimDemo() {
  const account = useMemo(() => privateKeyToAccount(generatePrivateKey()), []);
  const seller = useMemo(() => privateKeyToAccount(generatePrivateKey()), []);
  const impostor = useMemo(() => privateKeyToAccount(generatePrivateKey()), []);
  const [sellerCheck, setSellerCheck] = useState(null);
  const cases = useRef({});
  const [currentId, setCurrentId] = useState(null);
  const [log, setLog] = useState([]);
  const [, setTick] = useState(0);
  const push = (code, text) => setLog((l) => [{ id: `${Date.now()}-${Math.random()}`, code, text }, ...l].slice(0, 8));
  const sign = (hash) => account.signMessage({ message: { raw: hash } });
  const addEvent = async (c, ...args) => {
    const e = draftEvent(c, ...args);
    const hash = eventHash(e);
    c.events.push({ ...e, eventHash: hash, signature: await sign(hash) });
  };
  const done = () => setTick((t) => t + 1);

  const submit = async (d, opts = {}) => {
    if (!DEMO_FILE.claimTypes.includes(d.claimType)) return push(422, `Claim type "${d.claimType}" is not accepted on this endpoint. The pilot covers cancellations only.`);
    let cid;
    try { cid = computeClaimId(d); } catch (err) { return push(422, err.message); }
    const rh = requestHash(d);
    const existing = cases.current[cid];
    if (existing) {
      setCurrentId(cid);
      if (existing.requestHash !== rh) return push(409, 'Duplicate claim. This claim number was already used with different content. Use the next claim number for a new claim.');
      existing.agentHasReceipt = true;
      push(200, 'Repeat. Same claim ID, so you get the original receipt (same receivedAt) and the same case. No new case was opened.');
      return done();
    }
    const t = now();
    const base = { claimId: cid, requestHash: rh, caseRef: `CASE-${cid.slice(2, 10).toUpperCase()}`, receivedAt: t, firstStatusBy: t + DEMO_FILE.firstStatusBy * HOUR };
    const hash = receiptHash(base);
    const c = { agentHasReceipt: !opts.loseReceipt, pendingOrderCheck: !DEMO_ORDERS.includes(d.orderRef), claimId: cid, caseRef: base.caseRef, requestHash: rh, spokenCode: spokenCode(cid), contested: [], events: [], receipt: { ...base, receiptHash: hash, signature: await sign(hash) } };
    cases.current[cid] = c;
    if (!opts.slowMerchant) await acknowledgeCase(c);
    setCurrentId(cid);
    if (opts.loseReceipt) push(null, 'No reply reached the agent. The merchant got the claim, but its receipt was lost on the way back. A retry with the same claim ID is safe.');
    else if (opts.slowMerchant) push(201, `Created. Receipt returned for case ${c.caseRef}. No status yet. The merchant must add "received" by the first-status time.`);
    else push(201, `Created. Signed delivery receipt returned, case ${c.caseRef} opened${c.events.length > 1 ? ', order not matched' : ''}.`);
    return done();
  };

  async function acknowledgeCase(c) {
    await addEvent(c, 'received', 24);
    if (c.pendingOrderCheck) await addEvent(c, 'needs information', 48, { missingInfo: 'order not matched' });
  }

  const acknowledge = async () => {
    await acknowledgeCase(current);
    push(201, 'The merchant added "received" to the case. Delivery is now acknowledged.');
    done();
  };

  const current = currentId ? cases.current[currentId] : null;

  const checkStatus = () => {
    if (!current.events.length) return push(200, 'No status yet. The receipt is the only proof so far that the claim arrived.');
    const last = current.events[current.events.length - 1];
    const ttl = Math.max((last.nextUpdateBy || 0) - now(), 0);
    push(200, `Status: ${last.status}. Same answer until the next update time (Cache-Control: max-age=${ttl}). Checking early is never refused or slowed.`);
  };

  const decide = async (status, decisionCode, refundRoute = '') => {
    await addEvent(current, status, 0, { decisionCode, refundRoute });
    push(201, `A person added "${status}" (${decisionCode}) to the same case.`);
    done();
  };

  const contest = async () => {
    const decision = [...current.events].reverse().find(isDecision);
    if (!decision) return push(422, 'There is no decision to contest yet.');
    if (current.contested.includes(decision.eventHash)) return push(409, 'This decision has already been contested once. A second contest of the same decision is refused.');
    current.contested.push(decision.eventHash);
    await addEvent(current, 'contested', DEMO_FILE.contestWindow);
    push(201, `Contest opened. A person must decide it within ${DEMO_FILE.contestWindow} hours.`);
    return done();
  };

  const checkSeller = async (mode) => {
    if (mode === 'nofile') return setSellerCheck({ noFile: true, listed: false });
    const agent = mode === 'impostor' ? impostor : seller;
    const message = `I act for ${DEMO_FILE.merchant}`;
    const signer = await recoverMessageAddress({ message, signature: await agent.signMessage({ message }) });
    const listed = [seller.address].some((w) => w.toLowerCase() === signer.toLowerCase());
    setSellerCheck({ signer, listed, endpoint: DEMO_FILE.endpoints.claims, offeredEndpoint: IMPOSTOR_ENDPOINT });
  };

  return { sellerCheck, checkSeller, merchantKey: account.address, current, log, submit, checkStatus, decide, contest, acknowledge };
}
